import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';
import { RewardChip } from '@/features/guide-rewards/components/RewardChip';
import { adminGrowthApi, adminGrowthKeys, type AdminTransaction, type ReconcileResult } from '../api/admin-growth.api';
import { FilterBar, GrowthTable, fmtDateTime, fmtNum, humanize, useGrowthMutation } from './GrowthShared';

function AdjustDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [guideId, setGuideId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const adjust = useGrowthMutation(() => adminGrowthApi.adjust({ guideId: guideId.trim(), amount: Number(amount), reason: reason.trim() }), 'Adjustment recorded');
  const valid = guideId.trim().length >= 5 && Number(amount) !== 0 && Number.isInteger(Number(amount)) && reason.trim().length >= 3;

  return (
    <Dialog open={open} onClose={adjust.isPending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Adjust credits</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Alert severity="warning">Adjustments are new ledger entries — nothing is overwritten. Use a negative amount to deduct.</Alert>
        <TextField label="Guide ID (TRP-GDE-…)" value={guideId} onChange={(e) => setGuideId(e.target.value.toUpperCase())} />
        <TextField label="Credits (+/-)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <TextField label="Reason (shown in the guide's history)" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 300 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={adjust.isPending}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || adjust.isPending}
          onClick={() =>
            adjust.mutate(undefined, {
              onSuccess: () => {
                setGuideId('');
                setAmount('');
                setReason('');
                onClose();
              },
            })
          }
        >
          Record adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReconcileDialog({ tx, onClose }: { tx: AdminTransaction | null; onClose: () => void }) {
  const [result, setResult] = useState<ReconcileResult | null>(null);
  const check = useGrowthMutation((apply: boolean) => adminGrowthApi.reconcile(tx!.guideUserId, apply), 'Reconciliation complete');

  const run = (apply: boolean) => check.mutate(apply, { onSuccess: (data) => setResult(data) });
  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={Boolean(tx)} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700 }}>Reconcile wallet</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Recomputes {tx?.guide?.name ?? 'this guide'}'s balance from the ledger and compares it with the stored wallet.
        </Typography>
        {result && (
          result.inSync ? (
            <Alert severity="success">In sync — {fmtNum(result.ledger.available)} available, {fmtNum(result.ledger.lifetimeEarned)} lifetime.</Alert>
          ) : (
            <Alert severity={result.applied ? 'success' : 'warning'}>
              Stored {fmtNum(result.stored.available)} vs ledger {fmtNum(result.ledger.available)} (drift {fmtNum(result.drift.available)}).
              {result.applied ? ' The wallet was corrected to the ledger.' : ' Apply to correct the wallet.'}
            </Alert>
          )
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>Close</Button>
        <Button onClick={() => run(false)} disabled={check.isPending}>Check</Button>
        {result && !result.inSync && !result.applied && (
          <Button variant="contained" onClick={() => run(true)} disabled={check.isPending}>Apply ledger balance</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function GrowthCreditsPanel() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [type, setType] = useState('');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [reconcileTx, setReconcileTx] = useState<AdminTransaction | null>(null);

  const query = { page: page + 1, limit, search: search.trim() || undefined, source: source || undefined, type: type || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.transactions(query),
    queryFn: () => adminGrowthApi.transactions(query),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">The credits ledger is append-only. Click a row to reconcile that guide's wallet.</Typography>
        <Button variant="contained" startIcon={<TuneIcon />} onClick={() => setAdjustOpen(true)}>Adjust credits</Button>
      </Box>
      <FilterBar>
        <TextField size="small" label="Search guide" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} />
        <TextField size="small" select label="Source" value={source} onChange={(e) => { setSource(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['REFERRAL', 'TSHIRT_SELFIE', 'BOOKING_MILESTONE', 'PHOTO_MILESTONE', 'ADMIN_ADJUSTMENT', 'REVERSAL'].map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Type" value={type} onChange={(e) => { setType(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['CREDIT', 'REVERSAL', 'ADJUSTMENT'].map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
        </TextField>
      </FilterBar>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data?.items}
        rowKey={(r) => r._id}
        onRowClick={setReconcileTx}
        emptyText="No credit transactions yet."
        page={page}
        limit={limit}
        total={data?.total}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(0); }}
        columns={[
          { key: 'date', label: 'Date', render: (r) => fmtDateTime(r.createdAt) },
          { key: 'guide', label: 'Guide', render: (r) => <><Typography fontWeight={600} fontSize="0.875rem">{r.guide?.name ?? '—'}</Typography><Typography variant="caption" color="text.secondary">{r.guide?.guideId ?? ''}</Typography></> },
          { key: 'desc', label: 'Description', render: (r) => r.description },
          { key: 'source', label: 'Source', render: (r) => humanize(r.source) },
          { key: 'type', label: 'Type', render: (r) => <RewardChip label={humanize(r.type)} tone={r.type === 'CREDIT' ? 'success' : r.type === 'REVERSAL' ? 'error' : 'neutral'} /> },
          { key: 'amount', label: 'Credits', align: 'right', render: (r) => <Typography fontWeight={800} color={r.amount >= 0 ? 'success.dark' : 'error.main'}>{r.amount > 0 ? '+' : ''}{fmtNum(r.amount)}</Typography> },
        ]}
      />
      <AdjustDialog open={adjustOpen} onClose={() => setAdjustOpen(false)} />
      <ReconcileDialog tx={reconcileTx} onClose={() => setReconcileTx(null)} />
    </>
  );
}

export function GrowthAuditPanel() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [entityType, setEntityType] = useState('');
  const query = { page: page + 1, limit, entityType: entityType || undefined };
  const { data, isLoading, isError } = useQuery({
    queryKey: adminGrowthKeys.audit(query),
    queryFn: () => adminGrowthApi.auditLogs(query),
    placeholderData: keepPreviousData,
  });

  const summarize = (value?: Record<string, unknown>) =>
    value ? Object.entries(value).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ') : '';

  return (
    <>
      <FilterBar>
        <TextField size="small" select label="Entity" value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(0); }}>
          <MenuItem value="">All</MenuItem>
          {['Reward', 'RewardActivity', 'RewardCampaign', 'PhotoSubmission', 'MerchandiseRequest', 'MerchandiseItem', 'CreditsTransaction', 'CreditsWallet'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
      </FilterBar>
      <GrowthTable
        loading={isLoading}
        error={isError}
        rows={data?.items}
        rowKey={(r) => r._id}
        emptyText="No audit entries yet."
        page={page}
        limit={limit}
        total={data?.total}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(0); }}
        columns={[
          { key: 'date', label: 'When', render: (r) => fmtDateTime(r.createdAt) },
          { key: 'actor', label: 'Who', render: (r) => r.actorName ?? '—' },
          { key: 'action', label: 'Action', render: (r) => <b>{humanize(r.action)}</b> },
          { key: 'entity', label: 'Entity', render: (r) => <>{r.entityType}{r.guideName ? <Typography variant="caption" display="block" color="text.secondary">{r.guideName}</Typography> : null}</> },
          {
            key: 'change',
            label: 'Change',
            render: (r) => (
              <Box sx={{ maxWidth: 420, fontSize: '0.78rem', color: 'text.secondary', wordBreak: 'break-word' }}>
                {r.before && <div>Before — {summarize(r.before)}</div>}
                {r.after && <div>After — {summarize(r.after)}</div>}
                {r.reason && <div>Reason — {r.reason}</div>}
              </Box>
            ),
          },
        ]}
      />
    </>
  );
}
